import classNames from 'classnames'
import {
  DetailedHTMLProps,
  FC,
  InputHTMLAttributes,
  ReactNode,
  useRef,
  useState,
} from 'react'

import Colors from '~/app/lib/colors'
import Icon from '../Icon'
import Text from '../Text/Text'
import styles from './Input.module.scss'

interface InputProps
  extends Omit<
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    'prefix'
  > {
  label?: string
  error?: string | string[]
  suffixIcon?: ReactNode
  hint?: string
  prefix?: ReactNode
  showCount?: boolean
  inputClassName?: string
  inputFieldClassName?: string
  inputContainerClassName?: string
  variant?: 'border-bottom' | 'default' | 'border-less'
  onClear?: () => void
  labelExtra?: ReactNode
  labelClassName?: string
  onEnter?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

const Input: FC<InputProps> = ({
  label,
  className,
  error,
  suffixIcon,
  prefix,
  hint,
  showCount,
  inputClassName,
  inputFieldClassName,
  inputContainerClassName,
  variant = 'default',
  required,
  onClear,
  labelExtra,
  type = 'text',
  labelClassName,
  onEnter,
  ...rest
}) => {
  const contentLength = rest.value?.toString()?.length || 0
  const inputRef = useRef<HTMLInputElement>(null)
  const isPasswordType = type === 'password'
  const [showPassword, setShowPassword] = useState(false)
  const togglePasswordVisibility = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    setShowPassword((prev: boolean) => !prev)
    if (inputRef.current) {
      inputRef.current.type = showPassword ? 'text' : 'password'
    }
  }
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (onEnter) {
        onEnter(event)
      }
    }
  }

  return (
    <div
      className={classNames(styles.input_container, styles[variant], className, {
        [styles.disabled]: rest.disabled,
      })}
    >
      {label && (
        <div
          className={classNames('flex items-center justify-between gap-2', labelClassName)}
        >
          <label className="input-label">
            {label}
            {required && ' *'}
          </label>
          {labelExtra}
        </div>
      )}
      <div
        className={classNames(styles.input_field, inputFieldClassName, inputContainerClassName)}
      >
        {prefix && <div className={styles.prefix}>{prefix}</div>}
        <input
          ref={inputRef}
          className={classNames(styles.input, inputClassName)}
          type={type === 'password' ? (showPassword ? 'text' : 'password') : type}
          required={required}
          onKeyDown={handleKeyDown}
          {...rest}
        />
        {suffixIcon ? (
          <div className={styles.suffix_icon}>{suffixIcon}</div>
        ) : (
          isPasswordType && (
            <div
              className={classNames(styles.suffix_icon, styles.password_icon, 'cursor-pointer')}
              onClick={togglePasswordVisibility}
            >
              {!showPassword ? (
                <Icon name="eye" size={16} color={Colors.GREY_200} />
              ) : (
                <Icon name="eye-slash" size={16} color={Colors.GREY_200} />
              )}
            </div>
          )
        )}
        {onClear && rest.value ? (
          <Icon
            className={classNames(styles.suffix_icon, 'mr-2 cursor-pointer')}
            name="close"
            color={Colors.GREY_300}
            size={20}
            onClick={onClear}
          />
        ) : null}
        {showCount && (
          <Text className={classNames(styles.counter, 'ml-2 mr-3')} type="body-3" color="300">
            {contentLength}
            {rest.maxLength && `/${rest.maxLength}`}
          </Text>
        )}
      </div>

      {hint && <span className={styles.hint}>{hint}</span>}
      {error && <span className={styles.error}>{error}</span>}
    </div>
  )
}

export default Input
